import { Link } from "react-router-dom";
import { PROGRAMS, PILLARS } from "@/data/programs";
import DonateWidget from "@/components/DonateWidget";
import ProgramImage from "@/components/ProgramImage";
import TrustedPartners from "@/components/TrustedPartners";
import { Heart, Handshake, GraduationCap, ArrowRight, Sparkles, Users, Quote, Building2, School } from "lucide-react";

const HERO_IMG = "/img/hero2.jpg";

const STATS = [
  { value: "1,000+", label: "Students Impacted", icon: Users },
  { value: "6", label: "SEAA Programs", icon: Sparkles },
  { value: "8–16", label: "Ages We Serve", icon: GraduationCap },
  { value: "Multi-site", label: "Community Partners", icon: Building2 },
];

const PARTNERS = ["YMCA", "Boys & Girls Club", "Atlanta Public Library", "Best Buy Teen Tech Center", "Microsoft", "Recreation Centers"]; // eslint-disable-line no-unused-vars

export default function Home() {
  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative isolate overflow-hidden">
        <img src={HERO_IMG} alt="Youth in LP Foundation programs" className="absolute inset-0 h-full w-full object-cover object-[50%_28%]" />
        <div className="absolute inset-0 bg-brandnavy/65" />
        <div className="absolute inset-0 bg-gradient-to-t from-brandnavy/90 via-brandnavy/30 to-transparent" />
        <div className="relative mx-auto max-w-7xl px-5 sm:px-8 py-28 sm:py-36 lg:py-44">
          <div className="max-w-3xl animate-fade-up">
            <span className="inline-flex items-center gap-2 rounded-full bg-white/10 backdrop-blur px-4 py-1.5 text-xs font-medium tracking-[0.2em] uppercase text-white border border-white/20">
              <Sparkles className="h-3.5 w-3.5 text-accent" /> Science · Engineering · Athletics · Arts
            </span>
            <h1 className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight text-white leading-[1.05]">
              Preparing future leaders for real-world success.
            </h1>
            <p className="mt-6 text-lg text-slate-200 max-w-2xl leading-relaxed">
              LP Foundation is a community-driven youth development organization. Through structured, hands-on SEAA programs,
              we build performance readiness, applied academic skills, and workforce confidence in every young person.
            </p>
            <div className="mt-9 flex flex-wrap gap-4">
              <Link
                to="/support"
                data-testid="hero-donate-button"
                className="inline-flex items-center gap-2 rounded-full bg-accent px-7 py-3.5 font-semibold text-white shadow-lg transition-all duration-300 hover:bg-orange-400 hover:-translate-y-0.5"
              >
                <Heart className="h-5 w-5 fill-current" /> Donate
              </Link>
              <Link
                to="/partner"
                data-testid="hero-partner-button"
                className="inline-flex items-center gap-2 rounded-full bg-white px-7 py-3.5 font-semibold text-brandnavy transition-all duration-300 hover:-translate-y-0.5 hover:bg-slate-100"
              >
                <Handshake className="h-5 w-5" /> Partner With Us
              </Link>
              <Link
                to="/get-involved"
                data-testid="hero-enroll-button"
                className="inline-flex items-center gap-2 rounded-full border border-white/40 px-7 py-3.5 font-semibold text-white transition-all duration-300 hover:bg-white/10"
              >
                <GraduationCap className="h-5 w-5" /> Enroll Your Child
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* STATS */}
      <section className="bg-white border-b border-slate-100">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 py-12 grid grid-cols-2 lg:grid-cols-4 gap-8">
          {STATS.map((s) => (
            <div key={s.label} className="text-center" data-testid={`stat-${s.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
              <s.icon className="mx-auto h-7 w-7 text-accent" />
              <div className="mt-3 font-display text-3xl sm:text-4xl font-black text-brandnavy">{s.value}</div>
              <div className="mt-1 text-sm text-slate-500">{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* CHAMPIONS × LP FEATURE */}
      <section className="bg-brandnavy">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 py-16">
          <div className="relative overflow-hidden rounded-3xl border border-white/10 bg-white/5 p-8 sm:p-10">
            <div className="absolute -right-16 -top-16 h-64 w-64 rounded-full blur-3xl opacity-25" style={{ background: "#8BC53F" }} />
            <div className="relative flex flex-col gap-8 lg:flex-row lg:items-center lg:justify-between">
              <div className="max-w-2xl">
                <span className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-4 py-1.5 text-xs font-semibold tracking-[0.2em] uppercase text-white">
                  <Sparkles className="h-3.5 w-3.5" style={{ color: "#8BC53F" }} /> New Partnership
                </span>
                <div className="mt-4 flex items-center gap-3">
                  <span className="font-display font-black tracking-tight text-lg" style={{ color: "#8BC53F" }}>CHAMPIONS</span>
                  <span className="text-white/40 text-xl font-light">×</span>
                  <span className="font-display font-extrabold tracking-tight text-lg text-white">LP Foundation</span>
                </div>
                <h2 className="mt-4 font-display text-3xl sm:text-4xl font-black tracking-tight text-white">
                  Feed their future — a complete afterschool program for schools.
                </h2>
                <p className="mt-4 text-slate-300 leading-relaxed">
                  Nutritious meals, fitness, hands-on SEAA enrichment, and workforce readiness — delivered to your students
                  through a structured 3-month pathway experience.
                </p>
              </div>
              <Link
                to="/afterschool"
                data-testid="home-afterschool-cta"
                className="inline-flex shrink-0 items-center gap-2 rounded-full px-7 py-3.5 font-semibold text-brandnavy transition-all duration-300 hover:-translate-y-0.5"
                style={{ background: "#8BC53F" }}
              >
                <School className="h-5 w-5" /> Bring it to your school
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* MISSION */}
      <section className="mx-auto max-w-7xl px-5 sm:px-8 py-24">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <span className="text-xs font-semibold tracking-[0.2em] uppercase text-accent">Who We Are</span>
            <h2 className="mt-4 font-display text-3xl sm:text-4xl font-black tracking-tight text-brandnavy">
              We prepare students to perform — not just participate.
            </h2>
            <p className="mt-6 text-lg text-slate-600 leading-relaxed">
              We believe students perform best when they are prepared — mentally, emotionally, and academically. That's why
              every program begins with <span className="font-semibold text-brandnavy">performance readiness</span> and
              continues with structured, hands-on learning.
            </p>
            <div className="mt-6 rounded-2xl border-l-4 border-accent bg-orange-50/60 p-5">
              <p className="text-sm font-semibold uppercase tracking-wide text-accent">Our Mission</p>
              <p className="mt-2 text-slate-700 leading-relaxed">
                To prepare students for real-world success through structured, hands-on programs that build performance
                readiness, applied academic skills, and workforce confidence.
              </p>
            </div>
            <Link
              to="/programs"
              data-testid="mission-programs-link"
              className="mt-8 inline-flex items-center gap-2 font-semibold text-brandnavy hover:text-accent transition-colors"
            >
              Explore our programs <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          <div className="lg:col-span-5">
            <div className="grid grid-cols-2 gap-4">
              {PILLARS.map((p) => (
                <div
                  key={p.key}
                  className="rounded-3xl border border-slate-100 bg-white p-6 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-lg"
                >
                  <span className="inline-block h-3 w-12 rounded-full" style={{ background: p.color }} />
                  <h3 className="mt-4 font-display text-xl font-bold text-brandnavy">{p.label}</h3>
                  <p className="mt-1 text-sm text-slate-500">{p.blurb}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* PROGRAMS BENTO */}
      <section className="bg-slate-50 py-24">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <div className="max-w-2xl">
            <span className="text-xs font-semibold tracking-[0.2em] uppercase text-accent">SEAA Programs</span>
            <h2 className="mt-4 font-display text-3xl sm:text-4xl font-black tracking-tight text-brandnavy">
              Real-world exposure. Structured growth.
            </h2>
            <p className="mt-4 text-lg text-slate-600">
              We don't just introduce careers — we prepare students to perform. Six programs connect hands-on industry
              experiences with structured learning.
            </p>
          </div>
          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {PROGRAMS.map((p) => (
              <Link
                key={p.slug}
                to={`/programs/${p.slug}`}
                data-testid={`program-card-${p.slug}`}
                className="group relative overflow-hidden rounded-3xl bg-white border border-slate-100 shadow-sm transition-all duration-300 hover:-translate-y-1.5 hover:shadow-xl"
              >
                <div className="relative h-48 overflow-hidden">
                  <ProgramImage program={p} className="transition-transform duration-500 group-hover:scale-105" />
                </div>
                <div className="p-6">
                  <h3 className="font-display text-xl font-bold text-brandnavy group-hover:text-accent transition-colors">
                    {p.name}
                  </h3>
                  <p className="mt-1 text-xs font-medium uppercase tracking-wide" style={{ color: p.color }}>
                    {p.tag}
                  </p>
                  <p className="mt-2 text-sm text-slate-500 leading-relaxed">{p.short}</p>
                  <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-brandnavy">
                    Learn more <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* PATHWAYS */}
      <section className="mx-auto max-w-7xl px-5 sm:px-8 py-24">
        <div className="max-w-2xl">
          <span className="text-xs font-semibold tracking-[0.2em] uppercase text-accent">Get Involved</span>
          <h2 className="mt-4 font-display text-3xl sm:text-4xl font-black tracking-tight text-brandnavy">
            There's a place for you here.
          </h2>
          <p className="mt-4 text-lg text-slate-600">Find your pathway to making an impact with LP Foundation.</p>
        </div>
        <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {[
            { title: "Parents", text: "Enroll your child in a program built to help them perform.", to: "/get-involved", cta: "Enroll Now" },
            { title: "Schools & Rec Centers", text: "Bring SEAA enrichment to your students and members.", to: "/partner", cta: "Partner With Us" },
            { title: "Cities & Funders", text: "Invest in measurable youth outcomes across your community.", to: "/partner", cta: "Explore Partnership" },
            { title: "Donors", text: "Fund supplies, instructors, transportation & experiences.", to: "/support", cta: "Donate" },
            { title: "Volunteers", text: "Share your time and talents to inspire the next generation.", to: "/get-involved", cta: "Volunteer" },
            { title: "Sponsors", text: "Power community impact while building your brand.", to: "/partner", cta: "Become a Sponsor" },
          ].map((p) => (
            <Link
              key={p.title}
              to={p.to}
              data-testid={`pathway-${p.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              className="group rounded-3xl border border-slate-100 bg-white p-7 shadow-sm transition-all duration-300 hover:-translate-y-1.5 hover:shadow-xl"
            >
              <h3 className="font-display text-xl font-bold text-brandnavy">{p.title}</h3>
              <p className="mt-2 text-sm text-slate-600 leading-relaxed">{p.text}</p>
              <span className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-accent">
                {p.cta} <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </span>
            </Link>
          ))}
        </div>
      </section>

      {/* TRUSTED PARTNERS */}
      <TrustedPartners />

      {/* TESTIMONIAL */}
      <section className="bg-slate-50 py-24">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <Quote className="h-10 w-10 text-accent" />
            <blockquote className="mt-4 font-display text-2xl sm:text-3xl font-bold text-brandnavy leading-snug">
              "LP Foundation helped my son gain confidence and focus. He's excited about learning again."
            </blockquote>
            <p className="mt-4 text-slate-500">— Jennette B., Parent</p>
            <Link to="/testimonials" data-testid="home-testimonials-link" className="mt-6 inline-flex items-center gap-2 font-semibold text-brandnavy hover:text-accent transition-colors">
              Read more stories <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          <div className="lg:col-span-5">
            <div className="rounded-3xl bg-white border border-slate-100 p-8 shadow-sm">
              <p className="text-xs font-semibold tracking-[0.2em] uppercase text-accent">Community Outreach</p>
              <h3 className="mt-3 font-display text-xl font-bold text-brandnavy">More than programs.</h3>
              <p className="mt-3 text-sm text-slate-600 leading-relaxed">
                Beyond the classroom, we serve neighbors through our <span className="font-semibold text-slate-700">Feed the Family</span> hot-meal outreach and our annual <span className="font-semibold text-slate-700">Secret Santa</span> — because strong futures start with strong communities.
              </p>
              <Link to="/about" className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-brandnavy hover:text-accent transition-colors">
                See our impact <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </div>
        </div>
      </section>

      {/* DONATION SECTION */}
      <section className="bg-brandnavy py-24" id="donate">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 grid lg:grid-cols-2 gap-14 items-center">
          <div>
            <span className="text-xs font-semibold tracking-[0.2em] uppercase text-accent">Support Our Mission</span>
            <h2 className="mt-4 font-display text-3xl sm:text-4xl font-black tracking-tight text-white">
              When you give, you fund a child's confidence.
            </h2>
            <p className="mt-6 text-lg text-slate-300 leading-relaxed">
              You're not funding activities — you're funding a safe, structured learning environment, trained facilitators,
              and access to real-world skills for families regardless of income.
            </p>
            <ul className="mt-8 space-y-3">
              {[
                "A child's confidence & opportunity",
                "Safe, well-equipped learning spaces",
                "Trained, consistent facilitators",
                "Supplies, transportation & student experiences",
              ].map((t) => (
                <li key={t} className="flex items-center gap-3 text-slate-200">
                  <span className="grid h-6 w-6 place-items-center rounded-full bg-accent/20 text-accent">
                    <Heart className="h-3.5 w-3.5 fill-current" />
                  </span>
                  {t}
                </li>
              ))}
            </ul>
          </div>
          <DonateWidget compact />
        </div>
      </section>
    </div>
  );
}
